import { createSlice } from '@reduxjs/toolkit'
import {
  takePlo6SeatThunk,
  takePlo6ActionThunk,
  updatePlo6PlayerSittingStatusThunk,
  updatePlo6BuyInAmountThunk,
  changePlo6SeatThunk,
  getPlo6GameTablesThunk
} from 'redux-thunk/thunk/Plo6GameManagement.thunk'

const initialState = {
  gameData: null,
  gameState: 'idle',
  Plo6GameTables: [],
  currentSeat: null,
  playerActions: [],
  gamePot: 0,
  communityCards: [],
  currentPlayer: null,
  loading: false,
  // Specific loading states for different actions
  takingSeatLoading: false,
  takingActionLoading: false,
  error: null,
  pixi: {
    isAssetsLoaded: false,
    isInitialized: false
  }
}

const Plo6Slice = createSlice({
  name: 'plo6',
  initialState,
  reducers: {
    setGameData: (state, action) => {
      state.gameData = action.payload
    },
    setGameState: (state, action) => {
      state.gameState = action.payload
    },
    setCurrentSeat: (state, action) => {
      state.currentSeat = action.payload
    },
    addPlayerAction: (state, action) => {
      state.playerActions.push(action.payload)
    },
    setGamePot: (state, action) => {
      state.gamePot = action.payload
    },
    setCommunityCards: (state, action) => {
      state.communityCards = action.payload
    },
    setCurrentPlayer: (state, action) => {
      state.currentPlayer = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setPixiAssetsLoaded: (state, action) => {
      state.pixi.isAssetsLoaded = action.payload
    },
    setPixiInitialized: (state, action) => {
      state.pixi.isInitialized = action.payload
    },
    updateTableSeatOccupied: (state, action) => {
      const { tableId, seatsOccupied } = action.payload
      const tableIndex = state.Plo6GameTables.findIndex(table => String(table.id) === String(tableId))
      if (tableIndex !== -1) {
        state.Plo6GameTables[tableIndex].seatsOccupied = seatsOccupied
      }
    }
  },
  extraReducers: (builder) => {
    // Take Plo6 Seat
    builder
      .addCase(takePlo6SeatThunk.pending, (state) => {
        state.takingSeatLoading = true
        state.error = null
      })
      .addCase(takePlo6SeatThunk.fulfilled, (state, action) => {
        state.takingSeatLoading = false
        state.currentSeat = action.payload
      })
      .addCase(takePlo6SeatThunk.rejected, (state, action) => {
        state.takingSeatLoading = false
        state.error = action.error.message
      })

    // Take Plo6 Action
    builder
      .addCase(takePlo6ActionThunk.pending, (state) => {
        state.takingActionLoading = true
        state.error = null
      })
      .addCase(takePlo6ActionThunk.fulfilled, (state, action) => {
        state.takingActionLoading = false
        state.playerActions.push(action.payload)
        // Update pot if action affects it
        if (action.payload.potAmount) {
          state.gamePot = action.payload.potAmount
        }
      })
      .addCase(takePlo6ActionThunk.rejected, (state, action) => {
        state.takingActionLoading = false
        state.error = action.error.message
      })

    // Update Plo6 Player Sitting Status
    builder
      .addCase(updatePlo6PlayerSittingStatusThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePlo6PlayerSittingStatusThunk.fulfilled, (state, action) => {
        state.loading = false
        // Update game data with new sitting status
        if (state.gameData) {
          state.gameData.playerSittingStatus = action.payload
        }
      })
      .addCase(updatePlo6PlayerSittingStatusThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Update Plo6 Buy In Amount
    builder
      .addCase(updatePlo6BuyInAmountThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePlo6BuyInAmountThunk.fulfilled, (state, action) => {
        state.loading = false
        // Update game data with new buy-in amount
        if (state.gameData) {
          state.gameData.buyInAmount = action.payload
        }
      })
      .addCase(updatePlo6BuyInAmountThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Change Plo6 Seat
    builder
      .addCase(changePlo6SeatThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(changePlo6SeatThunk.fulfilled, (state, action) => {
        state.loading = false
        state.currentSeat = action.payload
      })
      .addCase(changePlo6SeatThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Get Plo6 Game Tables
    builder
      .addCase(getPlo6GameTablesThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getPlo6GameTablesThunk.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload?.data ?? action.payload
        // Backend returns { tables: [...], pagination: {...} }
        const tables = Array.isArray(data) ? data : (data?.tables ?? data?.rows ?? [])
        state.Plo6GameTables = tables.map(table => ({
          ...table,
          seatsOccupied: table.seatsOccupied !== undefined
            ? table.seatsOccupied
            : table?.tableSeats?.filter(seat => seat.userId !== null && seat.sittingOut === false).length || 0
        }))
      })
      .addCase(getPlo6GameTablesThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
  }
})

export const {
  setGameData,
  setGameState,
  setCurrentSeat,
  addPlayerAction,
  setGamePot,
  setCommunityCards,
  setCurrentPlayer,
  clearError,
  updateTableSeatOccupied,

  // pixi
  setPixiAssetsLoaded,
  setPixiInitialized
} = Plo6Slice.actions

export default Plo6Slice.reducer
